import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Layout,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EllipsisOutlined,
  ReloadOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import PaginatedTable from '../../components/PaginatedTable';
import FiltersTraductor from '../../components/compromisos/FiltersTraductor';
import EnvioTraducciones from '../../components/compromisos/EnvioTraducciones';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';
import { AxiosResponse } from 'axios';
import moment from 'moment';

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;

const DIRECTION_LABEL: Record<string, string> = {
  SC_TO_SP: 'SC-SP',
  SP_TO_SC: 'SP-SC',
};

const STATUS_COLOR: Record<string, string> = {
  COMPLETED:   'green',
  TRANSLATING: 'blue',
  PENDING:     'orange',
  FAILED:      'red',
  REVIEWED:    'cyan',
};

function Traductor() {

  const { t } = useTranslation();

  const initialColumns = [
    { title: t('scName'),            dataIndex: 'scName',               key: 'scName',               visible: true, fixed: 'left', width: 180 },
    { title: t('scNumber'),          dataIndex: 'scNumber',             key: 'scNumber',             visible: true, width: 85 },
    { title: t('flow'),              dataIndex: 'translationDirection', key: 'translationDirection', visible: true, width: 80,
      render: (v: string) => <Tag color="purple">{DIRECTION_LABEL[v] ?? v}</Tag> },
    { title: t('status'),            dataIndex: 'communicationStatus',  key: 'communicationStatus',  visible: true, width: 120,
      render: (v: string) => v ? <Tag color={STATUS_COLOR[v] ?? 'default'}>{t(v)}</Tag> : '-' },
    { title: t('scReference'),       dataIndex: 'scReference',          key: 'scReference',          visible: false, width: 110 },
    { title: t('comType'),           dataIndex: 'comType',              key: 'comType',              visible: true, width: 80 },
    { title: t('spNumber'),          dataIndex: 'spNumber',             key: 'spNumber',             visible: true, width: 85 },
    { title: t('spName'),            dataIndex: 'spName',               key: 'spName',               visible: true, width: 150 },
    { title: t('sourceLanguageLabel'), dataIndex: 'sourceLanguage',     key: 'sourceLanguage',       visible: false, width: 90 },
    { title: t('targetLanguageLabel'), dataIndex: 'targetLanguage',     key: 'targetLanguage',       visible: true, width: 90 },
    { title: t('requestedAt'),       dataIndex: 'requestedAt',          key: 'requestedAt',          visible: true, width: 130,
      render: (v: string) => v ? moment(v).format('DD/MM/YYYY HH:mm') : '-' },
    { title: t('notes'),             dataIndex: 'translatorNotes',      key: 'translatorNotes',      visible: false, width: 180 },
    { title: t('requestedBy'),       key: 'requestedBy',                                             visible: true, width: 150,
      render: (_: any, r: any) => r.userRequestName ? `${r.userRequestName} ${r.userRequestLastname}` : '-' },
    { title: t('translator'),        key: 'translator',                                              visible: false, width: 150,
      render: (_: any, r: any) => r.userAssignedName ? `${r.userAssignedName} ${r.userAssignedLastname}` : '-' },
    { title: t('statusNotes'),       dataIndex: 'communicationStatusNotes', key: 'communicationStatusNotes', visible: false, width: 200 },
  ];

  const { fetchApi, loading } = useFetch();
  const [columns, setColumns]                   = useState<Array<any>>(initialColumns);
  const [data, setData]                         = useState<Array<any>>([]);
  const [total, setTotal]                       = useState(0);
  const [current, setCurrent]                   = useState(1);
  const [pageSize, setPageSize]                 = useState(20);
  const [selectedRowKeys, setSelectedRowKeys]   = useState<React.Key[]>([]);
  const [openFilters, setOpenFilters]           = useState(false);
  const [filterApply, setFilterApply]           = useState<any>({});
  const [openEnvio, setOpenEnvio]               = useState(false);
  const [selectedRows, setSelectedRows]         = useState<any[]>([]);
  const [searchText, setSearchText]             = useState('');

  const appendFilters = (params: URLSearchParams, filters: any, search: string) => {
    (filters.statuses     || []).filter((v: any) => v != null).forEach((v: string) => params.append('statuses',     v));
    (filters.countryIds   || []).filter((v: any) => v != null).forEach((v: number) => params.append('countryIds',   String(v)));
    (filters.requesterIds || []).filter((v: any) => v != null).forEach((v: number) => params.append('requesterIds', String(v)));
    if (search?.trim()) params.set('search', search.trim());
  };

  const buildUrl = (page: number, size: number, filters: any, search = searchText) => {
    const params = new URLSearchParams();
    params.set('page', String(page - 1));
    params.set('size', String(size));
    params.set('sort', 'requestedAt,desc');
    appendFilters(params, filters, search);
    return `/translations/requests?${params.toString()}`;
  };

  const fetchData = (page: number, size: number, filters = filterApply, search = searchText) => {
    const dataSend: FetchApiInterface = {
      url: buildUrl(page, size, filters, search),
      method: 'GET',
    };
    fetchApi(dataSend).then(
      ({ data: res }: AxiosResponse<any>) => {
        setData(res?.content || []);
        setTotal(res?.totalElements || 0);
      }
    ).catch(() => {});
  };

  useEffect(() => {
    fetchData(current, pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrent(1);
    fetchData(1, pageSize, filterApply);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterApply]);

  const handleVisibleChange = (e: any, key: string) => {
    setColumns(cols => cols.map(c => c.key === key ? { ...c, visible: e.target.checked } : c));
  };

  const columnMenu = {
    items: columns.map(c => ({
      key: c.key,
      label: (
        <Checkbox checked={c.visible} onChange={e => handleVisibleChange(e, c.key)}>
          {c.title}
        </Checkbox>
      ),
    })),
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: any[]) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  const visibleColumns = columns.filter(c => c.visible);

  return (
    <Layout>
      <Content>
        <Row justify="center">
          <Col span={24} className="p-1">
            <Row gutter={[20, 20]}>

              {/* Header */}
              <Col span={24}>
                <Breadcrumb>
                  <Breadcrumb.Item>{t('compromisosPlan')}</Breadcrumb.Item>
                  <Breadcrumb.Item>{t('compromiso6')}</Breadcrumb.Item>
                  <Breadcrumb.Item>{t('translation')}</Breadcrumb.Item>
                </Breadcrumb>
                <Title level={3}>{t('translation')}</Title>
              </Col>

              {/* Card principal */}
              <Col span={24}>
                <Card className="border-radius">
                  <Row gutter={[10, 20]}>

                    {/* Barra de acciones */}
                    <Col span={24}>
                      <Row gutter={[10, 10]} justify="space-between" align="middle">

                        {/* Botones izquierda */}
                        <Col>
                          <Space wrap>
                            <FiltersTraductor
                              openModal={openFilters}
                              setOpenModal={setOpenFilters}
                              filterApply={filterApply}
                              onFinish={(values) => { setFilterApply(values); setOpenFilters(false); }}
                              cleanFilters={() => { setFilterApply({}); setOpenFilters(false); }}
                            />

                            <Button
                              type="primary"
                              icon={<TranslationOutlined />}
                              disabled={selectedRows.length === 0}
                              onClick={() => {
                                const slim = selectedRows.map((r: any) => ({ id: r.id, assignedId: r.assignedId, translationDirection: r.translationDirection }));
                                sessionStorage.setItem('traductor_selected_rows', JSON.stringify(slim));
                                window.open('/scuapp/traductor-detalle', '_blank');
                              }}
                            >
                              {t('translate')}
                            </Button>

                            <EnvioTraducciones
                              open={openEnvio}
                              setOpen={setOpenEnvio}
                              selectedRows={selectedRows}
                              onConfirm={() => {
                                setOpenEnvio(false);
                                setSelectedRows([]);
                                setSelectedRowKeys([]);
                                fetchData(current, pageSize);
                              }}
                            />
                          </Space>
                        </Col>

                        {/* Controles derecha */}
                        <Col>
                          <Row align="middle" gutter={[10, 10]}>
                            <Col>
                              <Search
                                placeholder={t('searchByScNumberOrName')}
                                allowClear
                                enterButton
                                style={{ width: 280 }}
                                onSearch={(value) => {
                                  setSearchText(value);
                                  setCurrent(1);
                                  fetchData(1, pageSize, filterApply, value);
                                }}
                                onChange={(e) => {
                                  if (!e.target.value) {
                                    setSearchText('');
                                    setCurrent(1);
                                    fetchData(1, pageSize, filterApply, '');
                                  }
                                }}
                              />
                            </Col>
                            <Col>
                              <Tooltip title={t('showHideColumns')}>
                                <Dropdown trigger={['click']} menu={columnMenu}>
                                  <Button type="primary" shape="circle" icon={<EllipsisOutlined />} />
                                </Dropdown>
                              </Tooltip>
                            </Col>
                            <Col>
                              <Tooltip title={t('synchronize')}>
                                <Button
                                  icon={<ReloadOutlined />}
                                  onClick={() => fetchData(current, pageSize)}
                                />
                              </Tooltip>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>

                    {/* Contador de seleccionadas */}
                    {selectedRowKeys.length > 0 && (
                      <Col span={24}>
                        <b>{t('selectedCount', { count: selectedRowKeys.length })}</b>
                      </Col>
                    )}

                    {/* Tabla */}
                    <Col span={24}>
                      <PaginatedTable
                        dataSource={data}
                        columns={visibleColumns}
                        rowKey="id"
                        tableId="traductor"
                        loading={loading}
                        scroll={{ x: 1800 }}
                        total={total}
                        current={current}
                        pageSize={pageSize}
                        rowSelection={{ type: 'checkbox', ...rowSelection }}
                        onRow={(record: any) => ({
                          style: { cursor: 'pointer' },
                          onClick: () => {
                            sessionStorage.setItem('traductor_selected_rows', JSON.stringify([{ id: record.id, assignedId: record.assignedId, translationDirection: record.translationDirection }]));
                            window.open('/scuapp/traductor-detalle', '_blank');
                          },
                        })}
                        onPaginationChange={(page, size) => {
                          setCurrent(page);
                          setPageSize(size);
                          fetchData(page, size);
                        }}
                        onPageSizeChange={(_page, size) => {
                          setCurrent(1);
                          setPageSize(size);
                          fetchData(1, size);
                        }}
                      />
                    </Col>

                  </Row>
                </Card>
              </Col>

            </Row>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}

export default Traductor;
